import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '../../supabase/types/database.types';
import { getSupabase } from '../lib/supabaseClient';

const PENDING_KEY = 'spc.pendingPath';

export function savePendingPathFromLocation(loc: Location): void {
  const path = loc.pathname;
  if (path.startsWith('/join/') || path.startsWith('/family/')) {
    try {
      localStorage.setItem(PENDING_KEY, path + loc.search + loc.hash);
    } catch {
      // ignore storage failures
    }
  }
}

export function consumePendingPath(): string | null {
  try {
    const v = localStorage.getItem(PENDING_KEY);
    if (v) localStorage.removeItem(PENDING_KEY);
    return v;
  } catch {
    return null;
  }
}

export function peekPendingPath(): string | null {
  try {
    return localStorage.getItem(PENDING_KEY);
  } catch {
    return null;
  }
}

export function clearPendingPath(): void {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    // ignore
  }
}

function requireClient(): SupabaseClient<Database> {
  const sb = getSupabase();
  if (!sb) throw new Error('backend_unconfigured');
  return sb;
}

export async function joinNeighborhood(inviteToken: string, familyName: string, firstName: string): Promise<void> {
  const sb = requireClient();
  const { data: session } = await sb.auth.getSession();
  const user = session.session?.user;
  if (!user) throw new Error('not_signed_in');
  // Try new signature with first_name
  let callErr: any | null = null;
  const { error } = await sb.rpc('join_neighborhood', {
    family_name: familyName,
    invite_token: inviteToken,
    first_name: firstName || null
  } as any);
  if (error) {
    callErr = error;
    const code = (error as any)?.code || '';
    const msg = (error as any)?.message || '';
    const isMissing = code === 'PGRST202' || code === '42883' || /function .* join_neighborhood/i.test(msg);
    if (!isMissing) throw callErr;
    // Fallback to old 2-arg version
    const { error: e2 } = await sb.rpc('join_neighborhood', {
      family_name: familyName,
      invite_token: inviteToken
    } as any);
    if (e2) throw e2;
    // Persist first name separately
    if (firstName && firstName.trim().length > 0) {
      const { error: updErr } = await sb
        .from('profiles')
        .update({ first_name: firstName.trim() })
        .eq('id', user.id);
      if (updErr) {
        throw new Error("We couldn't save your first name. Please try again.");
      }
    }
  }
}

export async function joinFamily(partnerToken: string, firstName: string): Promise<void> {
  const sb = requireClient();
  const { data: session } = await sb.auth.getSession();
  const user = session.session?.user;
  if (!user) throw new Error('not_signed_in');
  // Try new signature with first_name
  let callErr: any | null = null;
  const { error } = await sb.rpc('join_family', {
    partner_token: partnerToken,
    first_name: firstName || null
  } as any);
  if (error) {
    callErr = error;
    const code = (error as any)?.code || '';
    const msg = (error as any)?.message || '';
    const isMissing = code === 'PGRST202' || code === '42883' || /function .* join_family/i.test(msg);
    if (!isMissing) throw callErr;
    // Fallback to old 1-arg version
    const { error: e2 } = await sb.rpc('join_family', {
      partner_token: partnerToken
    } as any);
    if (e2) throw e2;
    if (firstName && firstName.trim().length > 0) {
      const { error: updErr } = await sb
        .from('profiles')
        .update({ first_name: firstName.trim() })
        .eq('id', user.id);
      if (updErr) {
        throw new Error("We couldn't save your first name. Please try again.");
      }
    }
  }
}

export type InviteLinks =
  | {
      neighborhood_invite_token: string;
      partner_invite_token: string;
      is_owner: boolean;
    }
  | null;

export async function getMyInviteLinks(): Promise<InviteLinks> {
  const sb = getSupabase();
  if (!sb) return null;
  try {
    const { data, error } = await sb.rpc('get_my_invite_links' as any);
    if (error) {
      const code = (error as any)?.code || '';
      const msg = (error as any)?.message || '';
      const isMissing = code === 'PGRST202' || code === '42883' || /function .* get_my_invite_links/i.test(msg);
      if (isMissing) return null; // graceful fallback
      throw error;
    }
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) return null;
    return {
      neighborhood_invite_token: row.neighborhood_invite_token as string,
      partner_invite_token: row.partner_invite_token as string,
      is_owner: !!row.is_owner
    };
  } catch (err) {
    // Rethrow non-missing errors for UI mapping
    throw err;
  }
}

